import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MatNativeDateModule, MatOptionModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';

import { TypeaheadModule } from 'ngx-bootstrap/typeahead';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { TooltipDirectiveModule, TrSpinnerButtonDirectiveModule } from '@toro/common/shared/directives';

import { FirstNamePipeModule } from '@toro/common/shared/pipes';

import { AlertMessageModule } from '@toro/common/shared/components/alert-message/alert-message.module';
import { FooterModule } from '@toro/common/shared/components/footer/footer.module';
import { HubLogoModule } from '@toro/common/shared/components/hub-logo/hub-logo.module';
import { HubNavModule } from '@toro/common/shared/components/hub-nav/hub-nav.module';
import { SvgIconModule } from '@toro/common/shared/components/svg-icon/svg-icon.module';
import { TrAccordionModule } from '@toro/common/shared/components/tr-accordion/tr-accordion.module';
import { TrCheckboxModule } from '@toro/common/shared/components/tr-forms-inputs/tr-checkbox/tr-checkbox.module';
import { TrLoadingOverlayModule } from '@toro/common/shared/components/tr-loading-overlay/tr-loading-overlay.module';
import { TrPlannedCheckoutInputModule } from '@toro/common/shared/components/tr-planned-checkout-input/tr-planned-checkout-input.module';
import { TrStepInputModule } from '@toro/common/shared/components/tr-step-input/tr-step-input.module';
import { TrWrapperTwoColumnsModule } from '@toro/common/shared/components/tr-wrapper-two-columns/tr-wrapper-left-column.module';
import { SantanderGuaranteeIncreaseModalModule } from '@toro/common/shared/components/santander-guarantee-increase-modal/santander-guarantee-increase-modal.module';
import { SantanderBannerModule } from '@toro/common/shared/components/santander-banner/santander-banner.module';

// Migrar
import { StocksExchangeWalletReducer } from '@toro/common/shared/state-management/stocks-exchange-wallet/stocks-exchange-wallet.reducer';
import { StocksWalletExchangeCheckoutReducer } from '../state-management/stocks-wallet-exchange-checkout/stocks-wallet-exchange-checkout.reducer';

import { StocksMarketReducer } from '@toro/common/shared/state-management/stocks-market/stocks-market.reducer';

import { StocksExchangeWalletEffects } from '@toro/common/shared/state-management/stocks-exchange-wallet/stocks-exchange-wallet.effects';
import { StocksMarketEffects } from '@toro/common/shared/state-management/stocks-market/stocks-market.effects';

import { DialogActionConfirmationComponent } from './stock/dialogs/dialog-action-confirmation/dialog-action-confirmation.component';
import { DialogBuyAndSellConfirmationComponent } from './stock/dialogs/dialog-buy-and-sell-confirmation/dialog-buy-and-sell-confirmation.component';
import { DialogDoneConfirmationComponent } from './stock/dialogs/dialog-done-confirmation/dialog-done-confirmation.component';
import { TrackingChartDialogComponent } from './stock/dialogs/dialog-graphic/dialog-graphic.component';
import { DialogHistoricComponent } from './stock/dialogs/dialog-historic/dialog-historic.component';
import { DialogSuccessBuyComponent } from './stock/dialogs/dialog-success-buy/dialog-success-buy.component';
import { DialogSuccessDoneComponent } from './stock/dialogs/dialog-success-done/dialog-success-done.component';
import { DialogSuccessSellComponent } from './stock/dialogs/dialog-success-sell/dialog-success-sell.component';
import { DialogSuccessWrapComponent } from './stock/dialogs/dialog-success-wrap/dialog-success-wrap.component';
import { GroupmentCardModalComponent } from './stock/dialogs/groupment-card-modal/groupment-card-modal.component';
import { ResumeGroupmentCardsDialogComponent } from './stock/dialogs/resume-groupment-cards-dialog/resume-groupment-cards-dialog.component';

import { BackendService } from '@toro/common/shared/services/backend/backend.service';
import { HttpService } from '@toro/common/shared/services/http/http.service';
import { NetworkService } from '@toro/common/shared/services/network.services';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import { StorageService } from '@toro/common/shared/services/storage.service';
import { UserManagerService } from '@toro/common/shared/services/users/user-manager.service';
import { UtilitiesService } from '@toro/common/shared/services/utilities.service';

import { ComponentsModule } from '../components/components.module';
import { GlobalModule } from '../global.module';
import { TrackingsService } from '../services/tracking.service';
import { DialogResumeMobileComponent } from './investment-portfolio/components/data-table-mobile/dialog-resume-mobile/dialog-resume-mobile.component';
import { OrderHistoricService } from '../shared/services/toro-lumen/order-historic.service';

export function loadSettings(service: SettingsService) {
    return () => service.loadEnvironmentSettings();
}

@NgModule({
    imports: [
        GlobalModule,
        ComponentsModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        HttpClientModule,
        RouterModule,
        TrSpinnerButtonDirectiveModule,
        TooltipDirectiveModule,
        HubNavModule,
        HubLogoModule,
        AlertMessageModule,
        TrWrapperTwoColumnsModule,
        FooterModule,
        SvgIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatInputModule,
        MatOptionModule,
        FirstNamePipeModule,
        TrLoadingOverlayModule,
        TrStepInputModule,
        TrAccordionModule,
        TrCheckboxModule,
        TrPlannedCheckoutInputModule,
        SantanderGuaranteeIncreaseModalModule,
        SantanderBannerModule,
        TypeaheadModule.forRoot(),
        StoreModule.forFeature('stocksExchange', {
            stocksMarket: StocksMarketReducer,
            wallet: StocksExchangeWalletReducer,
            walletCheckout: StocksWalletExchangeCheckoutReducer,
        }),
        EffectsModule.forRoot([]),
        EffectsModule.forFeature([
            StocksExchangeWalletEffects,
            StocksMarketEffects,
        ]),
    ],
    declarations: [
        DialogHistoricComponent,
        DialogActionConfirmationComponent,
        DialogBuyAndSellConfirmationComponent,
        DialogSuccessSellComponent,
        DialogSuccessBuyComponent,
        DialogDoneConfirmationComponent,
        DialogSuccessDoneComponent,
        DialogSuccessWrapComponent,
        DialogResumeMobileComponent,
        TrackingChartDialogComponent,
        ResumeGroupmentCardsDialogComponent,
        GroupmentCardModalComponent,
    ],
    entryComponents: [
        DialogHistoricComponent,
        DialogActionConfirmationComponent,
        DialogBuyAndSellConfirmationComponent,
        DialogSuccessSellComponent,
        DialogSuccessBuyComponent,
        DialogDoneConfirmationComponent,
        DialogSuccessDoneComponent,
        DialogSuccessWrapComponent,
        DialogResumeMobileComponent,
        TrackingChartDialogComponent,
        ResumeGroupmentCardsDialogComponent,
        GroupmentCardModalComponent,
    ],
    providers: [
        HttpClient,
        UtilitiesService,
        SettingsService,
        HttpService,
        StocksExchangeWalletEffects,
        BackendService,
        UserManagerService,
        NetworkService,
        StorageService,
        TrackingsService,
        OrderHistoricService,
        { provide: LOCALE_ID, useValue: 'pt' },
        // { provide: APP_INITIALIZER, useFactory: loadSettings, deps: [SettingsService], multi: true },
    ],
})
export class PartialsModule {
    constructor(private dateAdapter: DateAdapter<NativeDateAdapter>) {
        this.dateAdapter.setLocale('pt-BR');
    }
}
